import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { firestore } from '../config/firebaseConfig';
import { collection, getDocs, addDoc, query, where, orderBy, doc, deleteDoc, writeBatch, updateDoc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { MdEditNote, MdDeleteForever } from 'react-icons/md';
import './MinicursoManager.css';

const tenantId = localStorage.getItem('tenantId') || 'unknownTenant';

const MinicursoManager = () => {
    // Estados da tela
    const [minicursos, setMinicursos] = useState([]);
    const [temas, setTemas] = useState([]);
    const [pilulas, setPilulas] = useState([]);
    const [questoes, setQuestoes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCurso, setSelectedCurso] = useState(null);
    const [etapas, setEtapas] = useState([]);

    // Estados dos formulários
    const [newCursoNome, setNewCursoNome] = useState('');
    const [newCursoComando, setNewCursoComando] = useState('');
    const [newCursoTema, setNewCursoTema] = useState('');
    const [newTema, setNewTema] = useState('');
    const [conteudoToAdd, setConteudoToAdd] = useState('');
    const [selectedContentId, setSelectedContentId] = useState('');
    const [selectedContentLabel, setSelectedContentLabel] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [difficultyFilter, setDifficultyFilter] = useState('todos');
    const [formVisible, setFormVisible] = useState('none');

    // Estado para controlar o input do intervalo
    const [intervaloEmMinutos, setIntervaloEmMinutos] = useState(1440);
    const [isEditingInterval, setIsEditingInterval] = useState(false);

    // --- FUNÇÕES DE BUSCA DE DADOS ---

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const temasQuery = query(collection(firestore, 'temas'), where('tenantId', '==', tenantId), orderBy('nome'));
            const cursosQuery = query(collection(firestore, 'minicursos'), where('tenantId', '==', tenantId), where('status', '==', 'ativo'));
            const pilulasQuery = query(collection(firestore, 'pilulas'), where('tenantId', '==', tenantId));
            const questoesQuery = query(collection(firestore, 'questoes'), where('tenantId', '==', tenantId));

            const [temasSnapshot, cursosSnapshot, pilulasSnapshot, questoesSnapshot] = await Promise.all([
                getDocs(temasQuery),
                getDocs(cursosQuery),
                getDocs(pilulasQuery),
                getDocs(questoesQuery)
            ]);

            setTemas(temasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setMinicursos(cursosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setPilulas(pilulasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setQuestoes(questoesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        } catch (error) {
            console.error("Erro ao buscar dados iniciais:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchEtapas = useCallback(async (cursoId) => {
        if (!cursoId) return;
        const q = query(collection(firestore, 'etapasMinicurso'), where('minicursoId', '==', cursoId), orderBy('ordem'));
        const snapshot = await getDocs(q);
        
        const etapasData = snapshot.docs.map(docSnap => {
            const etapa = { id: docSnap.id, ...docSnap.data() };
            let contentDetails = { label: 'Conteúdo não encontrado', type: etapa.tipoConteudo };
            
            if (etapa.tipoConteudo === 'pilula') {
                const pilulaDoc = pilulas.find(p => p.id === etapa.conteudoId);
                if (pilulaDoc) contentDetails.label = pilulaDoc.titulo;
            } else if (etapa.tipoConteudo === 'questao') {
                const questaoDoc = questoes.find(q => q.id === etapa.conteudoId);
                if (questaoDoc) {
                    contentDetails.label = questaoDoc.enunciado;
                    contentDetails.type = questaoDoc.type;
                    if (questaoDoc.type === 'quiz') contentDetails.difficulty = questaoDoc.difficulty || 'N/D';
                }
            }
            return { ...etapa, ...contentDetails };
        });
        setEtapas(etapasData);
    }, [pilulas, questoes]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    useEffect(() => {
        if (selectedCurso) {
            fetchEtapas(selectedCurso.id);
            setIntervaloEmMinutos(selectedCurso.intervaloEtapasEmMinutos || 1440);
            setIsEditingInterval(false); // Garante que sempre comece no modo de visualização
        }
    }, [selectedCurso, fetchEtapas]);

    // --- HANDLERS ---
    const handleCreateTema = async (e) => {
        e.preventDefault();
        if (!newTema.trim()) return;
        await addDoc(collection(firestore, 'temas'), { nome: newTema, tenantId });
        setNewTema('');
        setFormVisible('none');
        fetchAllData();
    };

    const handleCreateCurso = async (e) => {
        e.preventDefault();
        if (!newCursoNome.trim() || !newCursoComando.trim() || !newCursoTema) return;
        await addDoc(collection(firestore, 'minicursos'), {
            nome: newCursoNome,
            comando: `INICIAR_${newCursoComando.toUpperCase()}`,
            tema: newCursoTema,
            tenantId: tenantId,
            status: 'ativo',
            createdAt: new Date(),
            intervaloEtapasEmMinutos: 1440,
        });
        setNewCursoNome('');
        setNewCursoComando('');
        setNewCursoTema('');
        setFormVisible('none');
        fetchAllData();
    };

    const handleSelectCurso = (curso) => setSelectedCurso(curso);
    
    const handleDeleteCurso = async (cursoId) => {
        try {
            const cursoRef = doc(firestore, 'minicursos', cursoId);
            await updateDoc(cursoRef, { status: 'inativo' });
            fetchAllData();
            if (selectedCurso?.id === cursoId) {
                setSelectedCurso(null);
            }
        } catch (error) {
            console.error("Erro ao arquivar o minicurso: ", error);
        }
    };

    const handleAddEtapa = async (e) => {
        e.preventDefault();
        if (!selectedCurso || !conteudoToAdd || !selectedContentId) return;

        const isDuplicate = etapas.some(etapa => etapa.conteudoId === selectedContentId);
        if (isDuplicate) {
            alert("Esta etapa já foi adicionada a este minicurso.");
            return;
        }

        const novaOrdem = etapas.length + 1;
        await addDoc(collection(firestore, 'etapasMinicurso'), {
            minicursoId: selectedCurso.id,
            ordem: novaOrdem,
            tipoConteudo: conteudoToAdd,
            conteudoId: selectedContentId,
            tenantId: tenantId,
        });
        fetchEtapas(selectedCurso.id);
        setConteudoToAdd('');
        setSelectedContentId('');
        setSelectedContentLabel('');
        setSearchTerm('');
    };

    const handleRemoveEtapa = async (etapaIdParaRemover) => {
        if (!selectedCurso) return;
        await deleteDoc(doc(firestore, 'etapasMinicurso', etapaIdParaRemover));
        const etapasRestantes = etapas.filter(etapa => etapa.id !== etapaIdParaRemover).sort((a, b) => a.ordem - b.ordem);
        const batch = writeBatch(firestore);
        etapasRestantes.forEach((etapa, index) => {
            const novaOrdem = index + 1;
            if (etapa.ordem !== novaOrdem) {
                const etapaRef = doc(firestore, 'etapasMinicurso', etapa.id);
                batch.update(etapaRef, { ordem: novaOrdem });
            }
        });
        await batch.commit();
        fetchEtapas(selectedCurso.id);
    };

    const handleUpdateIntervalo = async () => {
        if (!selectedCurso) return;
        const cursoRef = doc(firestore, 'minicursos', selectedCurso.id);
        try {
            await updateDoc(cursoRef, {
                intervaloEtapasEmMinutos: Number(intervaloEmMinutos)
            });
            setSelectedCurso(prev => ({ ...prev, intervaloEtapasEmMinutos: Number(intervaloEmMinutos) }));
            setMinicursos(prevCursos => prevCursos.map(c =>
                c.id === selectedCurso.id ? { ...c, intervaloEtapasEmMinutos: Number(intervaloEmMinutos) } : c
            ));
            toast.success('Intervalo atualizado com sucesso!'); 
            setIsEditingInterval(false); // Fecha o modo de edição
        } catch (error) {
            console.error("Erro ao atualizar o intervalo:", error);
            toast.error('Falha ao atualizar o intervalo.'); 
        }
    };

    const filteredPilulas = useMemo(() => {
        if (!selectedCurso?.tema) return [];
        const temaObj = temas.find(t => t.nome.trim().toLowerCase() === selectedCurso.tema.trim().toLowerCase());
        if (!temaObj) return [];
        const temaId = temaObj.id;
        const etapadIds = new Set(etapas.map(e => e.conteudoId));
        return pilulas.filter(p =>
            !etapadIds.has(p.id) &&
            Array.isArray(p.temas) && p.temas.includes(temaId) &&
            (searchTerm ? p.titulo.toLowerCase().includes(searchTerm.toLowerCase()) : true)
        );
    }, [pilulas, selectedCurso, searchTerm, temas, etapas]);
    
    const filteredQuestoes = useMemo(() => {
        const temaObj = temas.find(t => t.nome.trim().toLowerCase() === selectedCurso?.tema.trim().toLowerCase());
        const temaId = temaObj ? temaObj.id : null;
        const etapadIds = new Set(etapas.map(e => e.conteudoId));
        return questoes.filter(q => {
            if (etapadIds.has(q.id)) return false;
            const matchesSearchTerm = searchTerm ? q.enunciado.toLowerCase().includes(searchTerm.toLowerCase()) : true;
            if (!matchesSearchTerm) return false;
            if (difficultyFilter === 'pesquisa') {
                return q.type?.startsWith('pesquisa');
            }
            if (!temaId) return false;
            const matchesTema = Array.isArray(q.temas) && q.temas.includes(temaId);
            if (!matchesTema) return false;
            if (difficultyFilter !== 'todos') {
                return q.difficulty?.toLowerCase() === difficultyFilter;
            }
            return true;
        });
    }, [questoes, selectedCurso, searchTerm, difficultyFilter, temas, etapas]);

    const handleSelectContent = (id, label) => {
        setSelectedContentId(id);
        setSelectedContentLabel(label);
        setSearchTerm('');
    };

    const getEtapaDetails = (etapa) => {
        if (etapa.tipoConteudo === 'pilula') return { icon: '📖', label: 'Pílula' };
        if (etapa.tipoConteudo === 'questao') {
            if (etapa.type === 'quiz') return { icon: '✅', label: 'Quiz' };
            if (etapa.type && etapa.type.startsWith('pesquisa')) return { icon: '📊', label: 'Pesquisa' };
        }
        return { icon: '❓', label: 'Questão' };
    };

    const summary = useMemo(() => {
        if (!etapas || etapas.length === 0) return null;
        const counts = {
            pilulas: 0,
            pesquisas: 0,
            quizzes: { total: 0, facil: 0, medio: 0, dificil: 0, nd: 0 }
        };
        etapas.forEach(etapa => {
            if (etapa.tipoConteudo === 'pilula') counts.pilulas++;
            else if (etapa.type === 'quiz') {
                counts.quizzes.total++;
                const diff = etapa.difficulty?.toLowerCase();
                if (diff === 'fácil') counts.quizzes.facil++;
                else if (diff === 'médio') counts.quizzes.medio++;
                else if (diff === 'difícil') counts.quizzes.dificil++;
                else counts.quizzes.nd++;
            } else if (etapa.type?.startsWith('pesquisa')) {
                counts.pesquisas++;
            }
        });
        return counts;
    }, [etapas]);

    if (isLoading) return <div className="loading-indicator">Carregando...</div>;

    return (
        <div className="minicurso-manager">
            <h2>Gerenciador de Minicursos</h2>
            <div className="manager-layout">
                <div className="column column-left">
                    <h3>Cursos Abertos</h3>
                    <ul className="curso-list">
                        {minicursos.map(curso => (
                            <li key={curso.id}>
                                <span className="curso-info" onClick={() => handleSelectCurso(curso)}>
                                    {curso.nome} <span>({curso.comando})</span>
                                </span>
                                <div className="curso-actions">
                                    <MdEditNote
                                        size={22}
                                        onClick={() => handleSelectCurso(curso)}
                                        title="Editar"
                                    />
                                    <MdDeleteForever
                                        size={22}
                                        onClick={() => handleDeleteCurso(curso.id)}
                                        title="Excluir"
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                    <hr />
                    <div className="creation-actions">
                        <button className="creation-button" onClick={() => setFormVisible('curso')}>+ Novo Curso</button>
                        <button className="creation-button" onClick={() => setFormVisible('tema')}>+ Novo Tema</button>
                    </div>
                    {formVisible === 'curso' && (
                        <form onSubmit={handleCreateCurso} className="creation-form">
                            <h4>Criar Novo Minicurso</h4>
                            <input type="text" value={newCursoNome} onChange={(e) => setNewCursoNome(e.target.value)} placeholder="Nome do minicurso" required />
                            <input type="text" value={newCursoComando} onChange={(e) => setNewCursoComando(e.target.value)} placeholder="Comando (ex: FINANCAS)" required />
                            <select value={newCursoTema} onChange={(e) => setNewCursoTema(e.target.value)} required>
                                <option value="">Selecione um Tema</option>
                                {temas.map(tema => <option key={tema.id} value={tema.nome}>{tema.nome}</option>)}
                            </select>
                            <div className="form-button-group">
                                <button type="submit">Confirmar</button>
                                <button type="button" className="cancel-button" onClick={() => setFormVisible('none')}>Cancelar</button>
                            </div>
                        </form>
                    )}
                    {formVisible === 'tema' && (
                        <form onSubmit={handleCreateTema} className="creation-form">
                            <h4>Criar Novo Tema</h4>
                            <input type="text" value={newTema} onChange={(e) => setNewTema(e.target.value)} placeholder="Nome do novo tema" />
                            <div className="form-button-group">
                                <button type="submit">Confirmar</button>
                                <button type="button" className="cancel-button" onClick={() => setFormVisible('none')}>Cancelar</button>
                            </div>
                        </form>
                    )}
                </div>
                <div className="column column-right">
                    {selectedCurso ? (
                        <>
                            <div className="edit-header">
                                <h3>O curso {selectedCurso.nome} possui {etapas.length} {etapas.length === 1 ? 'etapa' : 'etapas'}</h3>
                                <button className="close-button" onClick={() => setSelectedCurso(null)}>&times;</button>
                            </div>
                            
                            <div className="intervalo-container">
                                {/* --- MODO DE VISUALIZAÇÃO --- */}
                                {!isEditingInterval && (
                                    <div className="intervalo-view-mode">
                                        <span>Intervalo entre etapas: <strong>{intervaloEmMinutos}</strong> minutos</span>
                                        <MdEditNote
                                            className="edit-icon"
                                            size={22}
                                            onClick={() => setIsEditingInterval(true)}
                                            title="Editar intervalo"
                                        />
                                    </div>
                                )}
                                {/* --- MODO DE EDIÇÃO --- */}
                                {isEditingInterval && (
                                    <div className="intervalo-edit-mode">
                                        <label htmlFor="intervalo">Intervalo (minutos):</label>
                                        <input
                                            type="number"
                                            id="intervalo"
                                            value={intervaloEmMinutos}
                                            onChange={(e) => setIntervaloEmMinutos(e.target.value)}
                                            className="intervalo-input"
                                            autoFocus
                                        />
                                        <button onClick={handleUpdateIntervalo} className="intervalo-button-ok">OK</button>
                                        <button onClick={() => setIsEditingInterval(false)} className="intervalo-button-cancel">Cancelar</button>
                                    </div>
                                )}
                            </div>

                            {summary && (
                                <div className="summary-dashboard">
                                    <div className="summary-card"><span>{summary.pilulas}</span><div className="summary-title">Pílulas</div></div>
                                    <div className="summary-card"><span>{summary.quizzes.total}</span><div className="summary-title">Quizzes</div><div className="summary-breakdown">{summary.quizzes.facil} F / {summary.quizzes.medio} M / {summary.quizzes.dificil} D</div></div>
                                    <div className="summary-card"><span>{summary.pesquisas}</span><div className="summary-title">Pesquisas</div></div>
                                </div>
                            )}
                            <ul className="etapa-list">
                                {etapas.map(etapa => {
                                    const details = getEtapaDetails(etapa);
                                    return (
                                        <li key={etapa.id}>
                                            <span className="etapa-icon">{details.icon}</span>
                                            <div className="etapa-info">
                                                <div className="etapa-tipo">{details.label}</div>
                                                <div className="etapa-conteudo">{etapa.label.substring(0, 60)}...</div>
                                            </div>
                                            {etapa.type === 'quiz' && (<span className={`etapa-dificuldade ${etapa.difficulty?.toLowerCase()}`}>{etapa.difficulty}</span>)}
                                            <button className="remove-etapa-button" onClick={() => handleRemoveEtapa(etapa.id)}>&times;</button>
                                        </li>
                                    );
                                })}
                            </ul>
                            <hr />
                            <form onSubmit={handleAddEtapa}>
                                <h4>Adicionar Nova Etapa</h4>
                                <select value={conteudoToAdd} onChange={e => { setConteudoToAdd(e.target.value); setSelectedContentId(''); setSelectedContentLabel(''); }}>
                                    <option value="">Selecione o tipo</option>
                                    <option value="pilula">Pílula</option>
                                    <option value="questao">Questão</option>
                                </select>
                                {conteudoToAdd === 'questao' && (
                                     <div className="difficulty-filters">
                                         <button type="button" className={difficultyFilter === 'todos' ? 'active' : ''} onClick={() => setDifficultyFilter('todos')}>Todos</button>
                                         <button type="button" className={difficultyFilter === 'fácil' ? 'active' : ''} onClick={() => setDifficultyFilter('fácil')}>Fácil</button>
                                         <button type="button" className={difficultyFilter === 'médio' ? 'active' : ''} onClick={() => setDifficultyFilter('médio')}>Médio</button>
                                         <button type="button" className={difficultyFilter === 'difícil' ? 'active' : ''} onClick={() => setDifficultyFilter('difícil')}>Difícil</button>
                                         <button type="button" className={difficultyFilter === 'pesquisa' ? 'active' : ''} onClick={() => setDifficultyFilter('pesquisa')}>Pesquisas</button>
                                     </div>
                                )}
                                {conteudoToAdd && !selectedContentId && (
                                    <div>
                                        <input
                                            type="text"
                                            className="search-input"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder={`Buscar em ${conteudoToAdd}s...`}
                                        />
                                        <ul className="search-results">
                                            {conteudoToAdd === 'pilula' && filteredPilulas.map(p => (<li key={p.id} onClick={() => handleSelectContent(p.id, p.titulo)}>{p.titulo}</li>))}
                                            {conteudoToAdd === 'questao' && filteredQuestoes.map(q => (
                                                <li key={q.id} className="search-result-item" onClick={() => handleSelectContent(q.id, q.enunciado)}>
                                                    <span>{q.enunciado.substring(0, 70)}...</span>
                                                    {q.type === 'quiz' && <span className={`etapa-dificuldade small ${q.difficulty?.toLowerCase()}`}>{q.difficulty}</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {selectedContentId && (
                                    <div className="selected-content">
                                        <span><strong>Selecionado:</strong> {selectedContentLabel.substring(0, 50)}...</span>
                                        <button type="button" onClick={() => { setSelectedContentId(''); setSelectedContentLabel(''); }}>Limpar</button>
                                    </div>
                                )}
                                <button type="submit" disabled={!conteudoToAdd || !selectedContentId}>Adicionar Etapa</button>
                            </form>
                        </>
                    ) : (
                        <p className="placeholder-text">Selecione um minicurso para editar ou crie um novo.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MinicursoManager;
